import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { CryptoClient } from "../cryptoClient.js";
import { onUnauthorized } from "./authEvents.js";

export type SessionState =
  | { readonly status: "locked" }
  | { readonly status: "unlocked"; readonly email: string; readonly client: CryptoClient }
  | { readonly status: "expired"; readonly email: string; readonly client: CryptoClient };

export interface SessionApi {
  readonly state: SessionState;
  /** Length of the master password used to unlock, checked against the policy minimum. Never the password. */
  readonly passwordLength: number | undefined;
  readonly begin: (email: string, client: CryptoClient, passwordLength?: number | undefined) => void;
  /** After a password change in settings. */
  readonly notePasswordLength: (length: number) => void;
  readonly resume: () => void;
  readonly clear: () => void;
}

const SessionContext = createContext<SessionApi | null>(null);

export function SessionProvider(props: { readonly children: ReactNode }): ReactNode {
  const [state, setState] = useState<SessionState>({ status: "locked" });
  const [passwordLength, setPasswordLength] = useState<number | undefined>(undefined);

  useEffect((): (() => void) => {
    return onUnauthorized((): void => {
      setState((prev: SessionState): SessionState =>
        prev.status === "unlocked" ? { status: "expired", email: prev.email, client: prev.client } : prev
      );
    });
  }, []);

  const begin = useCallback((email: string, client: CryptoClient, length?: number | undefined): void => {
    setPasswordLength(length);
    setState({ status: "unlocked", email, client });
  }, []);

  const notePasswordLength = useCallback((length: number): void => {
    setPasswordLength(length);
  }, []);

  const resume = useCallback((): void => {
    setState((prev: SessionState): SessionState =>
      prev.status === "expired" ? { status: "unlocked", email: prev.email, client: prev.client } : prev
    );
  }, []);

  const clear = useCallback((): void => {
    setPasswordLength(undefined);
    setState({ status: "locked" });
  }, []);

  const api = useMemo<SessionApi>(
    (): SessionApi => ({ state, passwordLength, begin, notePasswordLength, resume, clear }),
    [state, passwordLength, begin, notePasswordLength, resume, clear]
  );
  return <SessionContext.Provider value={api}>{props.children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error("useSession must be used inside SessionProvider.");
  }
  return ctx;
}
